import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { LineInfo } from './LineInfo';
import { SearchableDropdown } from './SearchableDropdown';
import { Button } from './buttons/Button';
import { axios } from '../api';
import { Close } from '../assets/Close';
import {
  Customer,
  DiscountCreationSuccessMessage,
  DiscountExclusiveType,
  Option,
  Order,
} from '../pages/types';
import {
  BOTTOM_TOAST,
  generateErrorMsg,
  generateFullName,
  getCountryFlag,
  mapSntApiDate,
} from '../utils';

type AdhocDiscountModalProps = {
  customer: Customer;
  order: Order;
  refreshCustomer: () => void;
  onConfirm: () => void;
  onCancel: () => void;
};

export const AdhocDiscountModal = ({
  customer,
  order,
  refreshCustomer,
  onCancel,
  onConfirm,
}: AdhocDiscountModalProps) => {
  const [selectedDiscountType, setSelectedDiscountType] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [discountReasonOptions, setDiscountReasonOptions] = useState<Option[]>([]);
  const [discountReason, setDiscountReason] = useState<Option | null>(null);
  const [isLoadingDiscountReasons, setIsLoadingDiscountReasons] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoadingDiscountReasons(true);
    axios
      .get('orders/discount_reasons')
      .then((response) => {
        setDiscountReasonOptions(response.data);
      })
      .catch((error) => {
        toast.error(generateErrorMsg(error), BOTTOM_TOAST);
      })
      .finally(() => {
        setIsLoadingDiscountReasons(false);
      });
  }, []);

  const handleSubmit = () => {
    setIsSubmitting(true);
    axios
      .post(`accounts/${customer.id}/create_exclusive_discount`, {
        discount_type: selectedDiscountType,
        order_id: order.id,
        discount_reason: discountReason?.value,
      })
      .then(() => {
        let toastMsg = `Discount applied to account.`;
        const messageKey = selectedDiscountType as keyof typeof DiscountCreationSuccessMessage;
        if (messageKey) {
          toastMsg = DiscountCreationSuccessMessage[messageKey];
        }
        toast.success(toastMsg, BOTTOM_TOAST);
        onConfirm();
        if (selectedDiscountType === DiscountExclusiveType.CX_10_PERCENT_FOR_THREE_ORDERS) {
          navigate(`/customer-lookup/${customer.id}`);
        } else {
          refreshCustomer();
        }
      })
      .catch((e) => {
        toast.error(generateErrorMsg(e), BOTTOM_TOAST);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 modal">
      <div className="bg-white rounded-lg shadow-md p-6 min-w-[700px] relative border">
        <Close className="absolute top-1 right-1 w-8 h-8 cursor-pointer" onClick={onCancel} />
        <h1 className="font-bold text-left">Apply discount</h1>
        <div className="space-y-0">
          <LineInfo
            title="Customer"
            value={`${getCountryFlag(customer.default_country)} ${generateFullName(
              customer.first_name,
              customer.last_name
            )} (${customer.email})`}
            className="mb-0"
          />
          <LineInfo title="Order" value={order.id} className="mb-0" />
          <LineInfo title="Order Date" value={mapSntApiDate(order.scheduled)} className="mb-0" />
        </div>
        <hr className="my-4" />
        <h3 className="font-bold text-left mb-1">
          Select the discount you want to apply to this order:
        </h3>
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="radio"
              name="discountType"
              value={DiscountExclusiveType.CX_10_PERCENT}
              className="mr-2"
              onChange={(e) => setSelectedDiscountType(e.target.value)}
            />
            <span>10% off 1 order</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="discountType"
              value={DiscountExclusiveType.CX_10_PERCENT_FOR_THREE_ORDERS}
              className="mr-2"
              onChange={(e) => setSelectedDiscountType(e.target.value)}
            />
            <span>10% off 3 orders (winback)</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="discountType"
              value={DiscountExclusiveType.CX_30_PERCENT}
              className="mr-2"
              onChange={(e) => setSelectedDiscountType(e.target.value)}
            />
            <span>30% off 1 order</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="discountType"
              value={DiscountExclusiveType.CX_50_PERCENT_MEAL_PLAN_PRODUCTS}
              className="mr-2"
              onChange={(e) => setSelectedDiscountType(e.target.value)}
            />
            <span>50% off 1 order (meal plan products only)</span>
          </label>
        </div>
        <div className="w-full pr-2 flex flex-col gap-2">
          <h3 className="font-bold text-left mb-1">Select the discount reason to proceed:</h3>
          {isLoadingDiscountReasons ? (
            <div className="flex items-center justify-center py-4">
              <div className="text-gray-500">Loading discount reasons...</div>
            </div>
          ) : (
            <SearchableDropdown
              options={discountReasonOptions}
              value={discountReason}
              onChange={(newSelection: any) => setDiscountReason(newSelection)}
              placeholder="Select discount reason"
              isLoading={isLoadingDiscountReasons}
            />
          )}
        </div>
        <div className="mt-[15px] w-full flex justify-center">
          <Button
            label="Apply Discount"
            variant="primary"
            isDisabled={!selectedDiscountType || isSubmitting || discountReason === null}
            onClick={() => handleSubmit()}
          />
        </div>
        {selectedDiscountType === DiscountExclusiveType.CX_10_PERCENT_FOR_THREE_ORDERS && (
          <h6 className="mt-[10px] text-center text-sm text-gray-500">
            Please note: This discount code will be applied to the next 3 orders.
          </h6>
        )}
      </div>
    </div>
  );
};
